<template>
  <div class="gantt-chart-container">
    <div class="assets-container">
      <div class="search-wrapper flex-grow-1">
        <span class="d-none d-md-inline">{{ $t('pages.project.show.search') }}</span>
        <proa-select
          v-model="filterUserId"
          :options="filterUserOptions"
          class="search-select"
        />
      </div>
      <div class="button-wrapper flex-grow-1">
        <proa-button @click.native="onClickChangePeriod(-1)">{{ $t('common.prevWeek') }}</proa-button>
        <proa-button @click.native="onClickChangePeriod(1)">{{ $t('common.nextWeek') }}</proa-button>
      </div>
    </div>
    <div class="gantt-chart-table-container" ref="ganttChartTableContainer">
      <div class="table-outer-wrapper">
        <div class="table-inner-scroll-wrapper" ref="ganttChartTableInnerScrollWrapper">
          <table class="gantt-chart-table">
            <thead>
              <tr class="top">
                <th ref="baseLeftColumn">{{ columns[0][0] | moment('YYYY') }}</th>
                <th v-for="(days, i) in columns" :key="i" colspan="7">
                  {{ days[0] | moment('MM/DD') }} 〜 {{ days[days.length - 1] | moment('MM/DD') }}
                </th>
              </tr>
              <tr class="day-of-week">
                <th></th>
                <th
                  v-for="(day, i) in columns.flat()"
                  :key="i"
                  :class="{
                    'holiday': [0, 6].includes(libMoment(day).day()),
                    'today': libMoment().isSame(libMoment(day), 'day')
                  }"
                >
                  {{ day | moment('ddd') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in rows" :key="i">
                <th
                  v-if="row.isTaskList"
                  :class="['task-list', { 'excluded': excludedTaskListIds.includes(row.id) }]"
                  @click="onClickTaskList(row.id)"
                >
                  {{ row.title }}
                </th>
                <th
                  v-else
                  @click="(e) => onClickTaskCard(e, row.id, true)"
                >
                  {{ row.title }}
                </th>
                <td
                  v-for="(data, di) in row.crossData"
                  :key="di"
                  :class="data.tdClasses"
                >
                  <div
                    :class="['column', data.classes]"
                    :style="data.style"
                    @click="(e) => onClickTaskCard(e, row.id, false)"
                  >
                    <img
                      v-if="data.classes.includes('start-only')"
                      :src="require('@/assets/images/icons/start_only_arrow.png')"
                      class="arrow"
                    >
                    <img
                      v-else-if="data.classes.includes('end-only')"
                      :src="require('@/assets/images/icons/end_only_arrow.png')"
                      class="arrow"
                    >
                  </div>
                </td>
              </tr>
            </tbody>
            <task-card-modal
              :invisible="taskCardModalId === null"
              :style="taskCardModalStyle"
              :taskCard="selectedTaskCard"
              ref="taskCardModal"
              @closeTaskCardModal="taskCardModalId = null"
            />
          </table>
          <table class="height-adjustment-table">
            <thead>
              <tr class="top">
                <th></th>
                <th v-for="(days, i) in columns" :key="i" colspan="7"></th>
              </tr>
              <tr>
                <th></th>
                <th
                  v-for="(day, i) in columns.flat()"
                  :key="i"
                  :class="{
                    'holiday': [0, 6].includes(libMoment(day).day()),
                    'today': libMoment().isSame(libMoment(day), 'day')
                  }"
                >
                </th>
              </tr>
            </thead>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import i18n from '@/i18n'
import moment from 'moment'
import Project from '@/store/models/Project'
import TaskList from '@/store/models/TaskList'
import TaskCard from '@/store/models/TaskCard'
import User from '@/store/models/User'

import TaskCardModal from '@/components/molecules/projectMenuTab/TaskCardModal'

export default {
  name: 'ProjectMenuGanttChart',
  components: {
    TaskCardModal
  },
  computed: {
    columns: function() {
      if (this.format === 'weekly') {
        const pastSunday = moment().startOf('week').add(this.formatDiff, 'weeks')
        return [...Array(4)].map((_, week) => {
          return [...Array(7)].map((_, day) => {
            return pastSunday.clone().add(week, 'weeks').add(day, 'days')
          })
        })
      } else {
        return []
      }
    },
    filterUserOptions: function() {
      return [{
        label: i18n.tc('common.allMember'),
        value: null
      }].concat(this.projectUsers.map((user) => {
        return {
          label: user.meta.displayName,
          value: user.id
        }
      }))
    },
    libMoment: () => moment,
    projectId: function() { return this.$route.params.id },
    project: function() { return Project.find(this.projectId) },
    projectUsers: function() { return User.findIn(this.project.userIds) },
    rows: function() {
      return this.taskLists.map((taskList) => {
        const formattedTaskList = [Object.assign(taskList, {
          isTaskList: true,
          crossData: this.columns.flat().map((column) => {
            const tdClasses = []
            if ([0, 6].includes(moment(column).day())) {
              tdClasses.push('holiday')
            }
            if (moment().isSame(moment(column), 'day')) {
              tdClasses.push('today')
            }
            return {
              classes: [],
              tdClasses: tdClasses
            }
          })
        })]
        const formattedTaskCards = this.excludedTaskListIds.includes(taskList.id) ? [] : this.taskCardsByTaskListId(taskList.id).map((taskCard) => {
          return Object.assign(taskCard, {
            crossData: this.columns.flat().map((column) => {
              const columnClasses= []
              const tdClasses = []
              let columnStyle = ''
              let enabledColumn = true

              if ([0, 6].includes(moment(column).day())) {
                tdClasses.push('holiday')
              }
              if (moment().isSame(moment(column), 'day')) {
                tdClasses.push('today')
              }
              if (column.isBetween(taskCard.startAt, taskCard.endAt, null, '[]')) {
                if (column.isSame(taskCard.startAt, 'day') && column.isSame(taskCard.endAt, 'day')) {
                  columnClasses.push('single-day')
                } else if (column.isSame(taskCard.startAt, 'day')) {
                  columnClasses.push('start')
                } else if (column.isSame(taskCard.endAt, 'day')) {
                  columnClasses.push('end')
                }
              } else if (column.isSame(taskCard.startAt, 'day')) {
                columnClasses.push('start-only')
              } else if (column.isSame(taskCard.endAt, 'day')) {
                columnClasses.push('end-only')
              } else {
                if ((!taskCard.startAt && !taskCard.endAt) ||
                   ((taskCard.startAt && !taskCard.endAt) && column.isAfter(taskCard.startAt, 'day')) ||
                   ((!taskCard.startAt && taskCard.endAt) && column.isBefore(taskCard.endAt, 'day'))) {
                  columnClasses.push('empty-column')
                }
                enabledColumn = false
              }

              if (enabledColumn) {
                columnClasses.push('enabled-column')
                if (taskCard.projectLabels[0]) {
                  columnStyle += `background-color: ${taskCard.projectLabels[0]?.color};`
                } else {
                  columnClasses.push('empty-label')
                }
              }
              return {
                classes: columnClasses,
                style: columnStyle,
                tdClasses: tdClasses
              }
            })
          })
        })
        return formattedTaskList.concat(formattedTaskCards)
      }).flat()
    },
    selectedTaskCard: function() {
      return TaskCard.find(this.taskCardModalId)
    },
    taskLists: function() {
      return TaskList.query().where((taskList) => {
        return taskList.projectId == this.projectId && taskList.listType != 'archive'
      }).orderBy('listIndex', 'asc').get()
    },
    taskCardsByTaskListId: function() {
      return function(taskListId) {
        return TaskCard.query().where((taskCard) => {
          return taskCard.taskListId == taskListId && (!this.filterUserId || taskCard.userIds.includes(Number(this.filterUserId)))
        }).orderBy('startAt', 'asc').get()
      }
    }
  },
  data() {
    return {
      excludedTaskListIds: [],
      filterUserId: null,
      format: 'weekly',
      formatDiff: 0,
      taskCardModalId: null,
      taskCardModalStyle: ''
    }
  },
  methods: {
    onClickChangePeriod: function(value) {
      this.formatDiff += value
      this.taskCardModalId = null
    },
    onClickTaskList: function(id) {
      if (this.excludedTaskListIds.includes(id)) {
        this.excludedTaskListIds.splice(this.excludedTaskListIds.indexOf(id), 1)
      } else {
        this.excludedTaskListIds.push(id)
      }
      this.taskCardModalId = null
    },
    onClickTaskCard: function(e, id, isTaskCardTitle) {
      this.taskCardModalId = id
      let leftPosition = 0
      let topPosition = 0

      // NOTE: クリック位置とモーダルの幅を計算し左右どちら側に表示するかを判定(タイトルクリックの場合は固定)
      if (isTaskCardTitle) {
        leftPosition = this.$refs.baseLeftColumn.offsetWidth + 10
      } else if (this.$refs.taskCardModal.$el.offsetWidth > this.$refs.ganttChartTableContainer.offsetLeft + this.$refs.ganttChartTableContainer.offsetWidth - e.pageX) {
        leftPosition = e.pageX - this.$refs.ganttChartTableContainer.offsetLeft - this.$refs.taskCardModal.$el.offsetWidth
      } else {
        leftPosition = e.pageX - this.$refs.ganttChartTableContainer.offsetLeft
      }

      // NOTE: クリック位置とモーダルの高さを計算し上下どちら側に表示するかを判定
      if (this.$refs.taskCardModal.$el.offsetHeight > this.$refs.ganttChartTableContainer.offsetTop + this.$refs.ganttChartTableContainer.offsetHeight - e.pageY) {
        topPosition = e.pageY - this.$refs.ganttChartTableContainer.offsetTop - this.$refs.taskCardModal.$el.offsetHeight
      } else {
        topPosition = e.pageY - this.$refs.ganttChartTableContainer.offsetTop
      }

      // NOTE: スクロール分を加算
      leftPosition += this.$refs.ganttChartTableInnerScrollWrapper.scrollLeft
      topPosition += this.$refs.ganttChartTableInnerScrollWrapper.scrollTop

      this.taskCardModalStyle = `
        left: ${leftPosition}px;
        top: ${topPosition}px;
      `
    }
  },
  watch: {
    filterUserId: function() {
      this.taskCardModalId = null
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/mediaquery";
@import "@/styles/variables";

.gantt-chart-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.assets-container {
  font-size: 13px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  .search-wrapper {
    display: flex;
    align-items: center;

    .search-select {
      max-width: 160px;

      @include mq(md) {
        margin-left: 15px;
      }
    }
  }

  .button-wrapper {
    text-align: right;

    button {
      font-size: 10px;
      margin-right: 5px;

      @include mq(md) {
        font-size: 13px;

        &:first-child {
          margin-right: 16px;
        }
      }
    }
  }
}

.gantt-chart-table-container {
  flex-grow: 1;
  height: 100%;
  margin: 0 -20px -20px -20px;

  .table-outer-wrapper {
    position: relative;
    height: 100%;
  }

  .table-inner-scroll-wrapper {
    position: absolute;
    height: 100%;
    width: 100%;
    overflow: auto;
    display: flex;
    flex-direction: column;
  }

  .gantt-chart-table {
    width: 100%;
    white-space: nowrap;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;

    th, td {
      height: 26px;

      &.holiday {
        background-color: #FAFAFA !important;
      }
    }

    tr th:first-child {
      width: 180px !important;

      @include mq(md) {
        width: 270px !important;
      }
    }

    thead tr {
      text-align: center;

      th {
        border: solid $light-border-color;
        font-size: 13px;
        font-weight: 300;
        z-index: 3;

        &:first-child {
          position: sticky;
          left: 0;
          z-index: 4;
        }

        &:last-child {
          border-right: none;
        }
      }

      &.top th {
        background-color: $background-color;
        color: $secondary-color;
        width: 180px;
        border-width: 1px 1px 1px 0;
        position: sticky;
        top: 0;

        &:first-child {
          font-weight: 600;
          padding-right: 10px;
          text-align: right;
        }
      }

      &.day-of-week th {
        background-color: white;
        color: $light-text-color;
        font-size: 12px;
        border-width: 0 1px 1px 0;
        position: sticky;
        top: 26px;

        &.today:after {
          content: "";
          position: absolute;
          width: 7px;
          height: 7px;
          top: calc(100% - 3.5px);
          left: calc(50% - 3.5px);
          background-color: $primary-color;
          border-radius: 50%;
        }
      }
    }

    tbody tr {

      &:last-child th, &:last-child td {
        border-bottom: none;
      }

      th {
        font-size: 13px;
        font-weight: 600;
        padding: 0 20px;
        background-color: white;
        border: solid $light-border-color;
        border-width: 0 1px 1px 0;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: pointer;

        &:first-child {
          position: sticky !important;
          left: 0;
          z-index: 3;
        }

        &.task-list {
          background-color: $secondary-color;
          color: white;
          font-size: 12px;
          position: relative;

          &:after {
            content: '';
            position: absolute;
            top: 50%;
            right: 10px;
            margin-top: -2.5px;
            width: 7px;
            height: 7px;
            border: 0px;
            border-bottom: solid 2px white;
            border-right: solid 2px white;
            transform: rotate(225deg);
          }

          &.excluded:after {
            margin-top: -4.5px;
            transform: rotate(45deg);
          }
        }
      }

      td {
        border-right: 1px solid $light-border-color;
        min-width: 28px;
        position: relative;
        padding: 0;

        &:last-child {
          border-right: 0;
        }

        &.today:after {
          content: "";
          position: absolute;
          height: 100%;
          width: 1px;
          top: 0;
          left: calc(50% - 0.5px);
          background-color: $primary-color;
          z-index: 2;
        }

        .column {
          width: calc(100% + 1px);
          height: 18px;
          position: absolute;
          top: 50%;
          left: -1px;
          transform: translate(0, -50%);
          z-index: 2;
          pointer-events: none;

          &.enabled-column {
            cursor: pointer;
            pointer-events: all;
          }

          &.start {
            border-radius: 2px 0 0 2px;
            border-width: 1px 0 1px 1px !important;
          }

          &.end {
            border-radius: 0 2px 2px 0;
            border-width: 1px 1px 1px 0 !important;
          }

          &.single-day, &.start-only, &.end-only {
            border-radius: 2px;
            border-width: 1px !important;

            .arrow {
              width: 17.67px;
              height: 10px;
              position: absolute;
              top: 50%;
              transform: translate(0, -50%);
            }
          }

          &.start-only .arrow {
            left: calc(100% - 3px);
          }

          &.end-only .arrow {
            right: calc(100% - 3px);
          }

          &.empty-label {
            background-color: #F7F7F7;
            border: 1px solid #CCCCCC;
            border-width: 1px 0;
          }

          &.empty-column {
            background-color: $background-color;
            border: none;
            z-index: 1;
          }
        }
      }
    }
  }

  .height-adjustment-table {
    flex-grow: 1;
    width: 100%;
    white-space: nowrap;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;

    tr.top th {
      width: 180px;
      height: 0px;
    }

    tr:not(.top) th {
      &:first-child {
        border-top: 1px solid $light-border-color;
        @include mq(md) {
          width: 270px !important;
        }
      }
    }

    tr th {
      background-color: white;
      padding: 0;
      position: relative;

      &:first-child {
        z-index: 3;
        position: sticky !important;
        left: 0;
        @include mq(md) {
          width: 270px !important;
        }
      }

      &:not(:last-child) {
        border-right: 1px solid $light-border-color;
      }

      &.holiday {
        background-color: #FAFAFA !important;
      }

      &.today:after {
        content: "";
        position: absolute;
        height: 100%;
        width: 1px;
        top: 0;
        left: calc(50% - 0.5px);
        background-color: $primary-color;
      }
    }
  }
}
</style>
